import * as DialogPrimitive from '@radix-ui/react-dialog'
import { X } from 'lucide-react'
import { createContext, useContext, type ComponentProps, type ComponentPropsWithoutRef, type ReactNode } from 'react'
import { isAboveModals, SCRIM, Z_DIALOG } from '@/lib/layers'

// shadcn-style Dialog wrapper built on Radix. Single shared primitive for
// every modal in the app — focus trap, Escape-to-close, restore-focus,
// scroll-lock and inert-background all come for free from Radix, so we
// don't have to hand-roll them per modal.
//
// Usage:
//   <Dialog open={open} onOpenChange={setOpen}>
//     <DialogContent title="Edit category" description="...">
//       ...form...
//       <DialogFooter>
//         <DialogClose asChild><button>Cancel</button></DialogClose>
//         <button onClick={save}>Save</button>
//       </DialogFooter>
//     </DialogContent>
//   </Dialog>

// Whether the open dialog is modal, for DialogContent's `aria-modal`. A
// non-modal one (the features pages' demo studio) leaves the page usable, and
// must not tell a screen reader otherwise.
const ModalContext = createContext(true)

export function Dialog({ modal = true, ...props }: ComponentProps<typeof DialogPrimitive.Root>) {
  return (
    <ModalContext.Provider value={modal}>
      <DialogPrimitive.Root modal={modal} {...props} />
    </ModalContext.Provider>
  )
}
export const DialogTrigger = DialogPrimitive.Trigger
export const DialogClose = DialogPrimitive.Close
export const DialogPortal = DialogPrimitive.Portal

interface DialogContentProps extends ComponentPropsWithoutRef<typeof DialogPrimitive.Content> {
  /** Visible heading rendered at the top of the dialog. Required for a11y. */
  title: string
  /** Optional one-line description shown below the title. */
  description?: ReactNode
  /** Hide the title visually but keep it in the a11y tree. */
  hideTitle?: boolean
  /** Tailwind size variant; default is sm (~28rem). */
  size?: 'sm' | 'md' | 'lg' | 'xl'
  /** When true (default), shows a close button in the top-right. */
  showClose?: boolean
  /**
   * A header of the dialog's own, drawn in the title row beside the close
   * button — for a heading richer than text (a logo). Pair with `hideTitle`:
   * the title stays for screen readers. No empty title row above it.
   */
  header?: ReactNode
  /**
   * Where the dialog mounts (Radix Portal `container`); default document.body.
   * The features showcase mounts one inside its own frame container.
   */
  container?: HTMLElement | null
}

const SIZE_CLASSES: Record<NonNullable<DialogContentProps['size']>, string> = {
  sm: 'max-w-md',
  md: 'max-w-lg',
  lg: 'max-w-2xl',
  xl: 'max-w-4xl',
}

export function DialogContent({
  title,
  description,
  hideTitle = false,
  size = 'sm',
  showClose = true,
  header,
  container,
  className = '',
  children,
  ...props
}: DialogContentProps) {
  const modal = useContext(ModalContext)
  return (
    <DialogPortal container={container ?? undefined}>
      <DialogPrimitive.Overlay
        // The scrim token, not `bg-black/70`: light mode swaps --color-black
        // to white, which turned this into a 70% WHITE veil.
        className={`fixed inset-0 ${Z_DIALOG} ${SCRIM} data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=open]:fade-in-0 data-[state=closed]:fade-out-0`}
      />
      <DialogPrimitive.Content
        {...props}
        // Said out loud: Radix makes the page behind inert but leaves the
        // attribute off, and the guided walkthrough reads it to know that a
        // step's field is inside this window, not on the page behind it.
        // Only when it IS modal: a non-modal dialog leaves the page usable.
        aria-modal={modal ? 'true' : undefined}
        // A press on the walkthrough's callout ("Next", "End tour") is not
        // the user leaving this form — closing it there threw away what they
        // had typed. Nor is a press in the live support chat, which floats
        // above every modal so a stuck user can still ask for help.
        onInteractOutside={e => {
          if (isAboveModals(e.target)) {
            e.preventDefault()
            return
          }
          props.onInteractOutside?.(e)
        }}
        // The `.card` class is only defined in admin CSS, so on public/marketing
        // pages (e.g. /apply) the dialog had a transparent background. Inline
        // the solid background + border here so the Dialog is self-contained
        // and renders consistently everywhere it's used.
        // Theme tokens where they exist (admin, portal, console — light or
        // dark), the old dark values where they do not (public pages).
        style={{
          background: 'var(--wb-surface, #0a0a0a)',
          border: '1px solid var(--wb-line, rgba(255, 255, 255, 0.08))',
          boxShadow: '0 4px 24px rgba(0, 0, 0, 0.2)'
        }}
        className={`fixed left-1/2 top-1/2 ${Z_DIALOG} -translate-x-1/2 -translate-y-1/2 w-full ${SIZE_CLASSES[size]} max-h-[90vh] overflow-y-auto rounded-xl p-6 space-y-4 mx-4 focus:outline-none data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=open]:fade-in-0 data-[state=closed]:fade-out-0 data-[state=open]:zoom-in-95 data-[state=closed]:zoom-out-95 ${className}`}
      >
        <div className="flex items-start justify-between gap-3" data-slot="dialog-header">
          <div className="min-w-0 flex-1">
            <DialogPrimitive.Title className={hideTitle ? 'sr-only' : 'text-base font-semibold text-[var(--wb-text,#e2e8f0)]'}>
              {title}
            </DialogPrimitive.Title>
            {description && (
              <DialogPrimitive.Description className="mt-1 text-xs" style={{ color: 'var(--wb-muted, rgba(226, 232, 240, 0.65))' }}>
                {description}
              </DialogPrimitive.Description>
            )}
            {header}
          </div>
          {showClose && (
            <DialogPrimitive.Close
              aria-label="Close"
              className="rounded-md p-1 text-[var(--wb-muted,rgba(255,255,255,0.6))] hover:text-[var(--wb-text,#fff)] hover:bg-[rgb(var(--wb-ink-rgb,255_255_255)/0.05)] transition-colors flex-shrink-0"
            >
              <X size={16} aria-hidden="true" />
            </DialogPrimitive.Close>
          )}
        </div>
        {children}
      </DialogPrimitive.Content>
    </DialogPortal>
  )
}

export function DialogFooter({ children, className = '' }: { children: ReactNode; className?: string }) {
  return (
    <div className={`flex items-center justify-end gap-2 pt-2 ${className}`}>
      {children}
    </div>
  )
}
